import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

/**
 * ListContextMenu - Reusable right-click context menu for list rows.
 * Portal-based, positioned at cursor with viewport clamping.
 * Same pattern as DiaryV2 ContextMenu.
 *
 * Props:
 *   x        (number)   - clientX from the right-click event
 *   y        (number)   - clientY from the right-click event
 *   items    (Array)    - [{ label, icon, action, danger }]
 *   onAction (function) - Called with the action string when an item is clicked
 *   onClose  (function) - Called to dismiss the menu
 */
function ListContextMenu({ x, y, items, onAction, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: y, left: x });

  // Clamp position to viewport
  useEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const pad = 8;
    let top = y;
    let left = x;
    if (top + rect.height > window.innerHeight - pad) top = window.innerHeight - rect.height - pad;
    if (left + rect.width > window.innerWidth - pad) left = window.innerWidth - rect.width - pad;
    if (top < pad) top = pad;
    if (left < pad) left = pad;
    setPos({ top, left });
  }, [x, y]);

  // Close on outside click, escape, or scroll
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        e.stopPropagation();
        e.preventDefault();
        const swallowClick = (ev) => {
          ev.stopPropagation();
          ev.preventDefault();
          document.removeEventListener('click', swallowClick, true);
        };
        document.addEventListener('click', swallowClick, true);
        setTimeout(() => document.removeEventListener('click', swallowClick, true), 400);
        onClose();
      }
    };
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    const handleScroll = () => onClose();

    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('keydown', handleEsc);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('keydown', handleEsc);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [onClose]);

  return ReactDOM.createPortal(
    <div ref={ref} className="dv2-context-menu" style={{ top: pos.top, left: pos.left }}>
      {items.map((item, i) => {
        if (item.divider) {
          return <div key={i} className="dv2-context-divider" />;
        }
        return (
          <button
            key={i}
            className={`dv2-context-item ${item.danger ? 'dv2-context-item--danger' : ''}`}
            onClick={() => { onAction(item.action); onClose(); }}
          >
            {item.icon}
            {item.label}
          </button>
        );
      })}
    </div>,
    document.body
  );
}

export default ListContextMenu;
